import { useState } from 'react';
import { ArrowDown, ArrowUp, ChevronRight, Clock, Pencil, Plus, Trash2, X } from 'lucide-react';
import type { SectionBlock, SectionBlockInput, SectionBlockType, SectionBlockUpdateInput } from '@unitly/contracts';
import { TextArea, TextField } from '../../ui/primitives.tsx';

/** Turns a YouTube/Vimeo link into its embeddable form; a direct video file plays inline; anything
 * else (e.g. a Drive/Loom share link) falls back to a plain "Apri video" link — still real, just not
 * embeddable inline without that provider's own player. */
function videoEmbed(url: string): { kind: 'iframe' | 'video' | 'link'; src: string } {
  try {
    const u = new URL(url);
    if (/(^|\.)youtube\.com$/.test(u.hostname) && u.searchParams.get('v')) {
      return { kind: 'iframe', src: `https://www.youtube.com/embed/${u.searchParams.get('v')}` };
    }
    if (u.hostname === 'youtu.be' && u.pathname.length > 1) {
      return { kind: 'iframe', src: `https://www.youtube.com/embed/${u.pathname.slice(1)}` };
    }
    if (/(^|\.)vimeo\.com$/.test(u.hostname) && u.pathname.length > 1) {
      return { kind: 'iframe', src: `https://player.vimeo.com/video/${u.pathname.split('/').filter(Boolean).pop()}` };
    }
    if (/\.(mp4|webm|ogg)$/i.test(u.pathname)) return { kind: 'video', src: url };
    return { kind: 'link', src: url };
  } catch {
    return { kind: 'link', src: url };
  }
}

function BlockView({ block }: { block: SectionBlock }) {
  if (block.type === 'text') {
    const { body } = block.content as { body: string };
    return <div className="block-body">{body}</div>;
  }
  if (block.type === 'table') {
    const { columns, rows } = block.content as { columns: string[]; rows: string[][] };
    return (
      <div className="block-table-wrap">
        <table className="block-table">
          <thead><tr>{columns.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => <tr key={i}>{columns.map((_, j) => <td key={j}>{r[j] ?? ''}</td>)}</tr>)}</tbody>
        </table>
      </div>
    );
  }
  const { url, caption } = block.content as { url: string; caption?: string | null };
  const embed = videoEmbed(url);
  return (
    <div className="block-video-wrap">
      {embed.kind === 'link' ? (
        <a className="btn btn-secondary btn-sm" href={embed.src} target="_blank" rel="noreferrer">Apri video</a>
      ) : (
        <div className="block-video">
          {embed.kind === 'iframe'
            ? <iframe src={embed.src} title={block.title ?? 'Video'} allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
            : <video src={embed.src} controls />}
        </div>
      )}
      {caption && <div className="block-video-caption">{caption}</div>}
    </div>
  );
}

/** `block.type` never changes for a mounted block, but hooks must still be unconditional: every
 * field is declared upfront and only the relevant ones are rendered/submitted per type. */
function EditBlockForm({ block, onCancel, onSave, pending }: { block: SectionBlock; onCancel: () => void; onSave: (patch: SectionBlockUpdateInput) => void; pending: boolean }) {
  const [title, setTitle] = useState(block.title ?? '');
  const [body, setBody] = useState(block.type === 'text' ? (block.content as { body: string }).body : '');
  const [columns, setColumns] = useState(block.type === 'table' ? (block.content as { columns: string[] }).columns : []);
  const [rows, setRows] = useState(block.type === 'table' ? (block.content as { rows: string[][] }).rows : []);
  const [url, setUrl] = useState(block.type === 'video' ? (block.content as { url: string }).url : '');
  const [caption, setCaption] = useState(block.type === 'video' ? ((block.content as { caption?: string | null }).caption ?? '') : '');

  const disabled = block.type === 'text' ? !body.trim() : block.type === 'table' ? columns.every((c) => !c.trim()) : !url.trim();
  const submit = () => {
    if (block.type === 'text') onSave({ title: title.trim() || null, content: { body: body.trim() } });
    else if (block.type === 'table') onSave({ title: title.trim() || null, content: { columns, rows } });
    else onSave({ title: title.trim() || null, content: { url: url.trim(), caption: caption.trim() || null } });
  };

  return (
    <div className="block-body" style={{ paddingTop: 12 }}>
      <div className="form">
        <TextField label="Titolo (facoltativo)" value={title} onChange={(e) => setTitle(e.target.value)} />
        {block.type === 'text' && <TextArea label="Testo" rows={5} value={body} onChange={(e) => setBody(e.target.value)} />}
        {block.type === 'table' && <TableGridEditor columns={columns} rows={rows} onChange={(c, r) => { setColumns(c); setRows(r); }} />}
        {block.type === 'video' && (
          <>
            <TextField label="Link video" placeholder="https://www.youtube.com/watch?v=..." value={url} onChange={(e) => setUrl(e.target.value)} />
            <TextField label="Didascalia (facoltativa)" value={caption} onChange={(e) => setCaption(e.target.value)} />
          </>
        )}
        <FormActions onCancel={onCancel} pending={pending} disabled={disabled} onSave={submit} />
      </div>
    </div>
  );
}

function FormActions({ onCancel, onSave, pending, disabled }: { onCancel: () => void; onSave: () => void; pending: boolean; disabled: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}><X size={14} /> Annulla</button>
      <button type="button" className="btn btn-primary btn-sm" disabled={disabled || pending} onClick={onSave}>Salva</button>
    </div>
  );
}

function TableGridEditor({ columns, rows, onChange }: { columns: string[]; rows: string[][]; onChange: (columns: string[], rows: string[][]) => void }) {
  const setCol = (i: number, v: string) => onChange(columns.map((c, j) => (j === i ? v : c)), rows);
  const setCell = (r: number, c: number, v: string) => onChange(columns, rows.map((row, i) => (i === r ? row.map((cell, j) => (j === c ? v : cell)) : row)));
  return (
    <div className="grid-editor">
      <span className="hint" style={{ padding: 0 }}>Colonne</span>
      <div className="grid-editor-row">
        {columns.map((c, i) => <input key={i} className="input" value={c} placeholder={`Colonna ${i + 1}`} onChange={(e) => setCol(i, e.target.value)} />)}
        <button type="button" className="icon-btn muted" aria-label="Aggiungi colonna" onClick={() => onChange([...columns, ''], rows.map((r) => [...r, '']))}><Plus size={15} /></button>
        {columns.length > 1 && (
          <button type="button" className="icon-btn muted" aria-label="Rimuovi ultima colonna" onClick={() => onChange(columns.slice(0, -1), rows.map((r) => r.slice(0, -1)))}><X size={15} /></button>
        )}
      </div>
      <span className="hint" style={{ padding: 0 }}>Righe</span>
      {rows.map((row, r) => (
        <div className="grid-editor-row" key={r}>
          {columns.map((_, c) => <input key={c} className="input" value={row[c] ?? ''} onChange={(e) => setCell(r, c, e.target.value)} />)}
          <button type="button" className="icon-btn muted" aria-label="Rimuovi riga" onClick={() => onChange(columns, rows.filter((_, i) => i !== r))}><X size={15} /></button>
        </div>
      ))}
      <button type="button" className="btn btn-plain btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => onChange(columns, [...rows, columns.map(() => '')])}>
        <Plus size={15} /> Aggiungi riga
      </button>
    </div>
  );
}

function BlockRow({ block, canManage, onUpdate, onDelete }: { block: SectionBlock; canManage: boolean; onUpdate: (patch: SectionBlockUpdateInput) => void; onDelete: () => void }) {
  const [open, setOpen] = useState(block.type !== 'text');
  const [editing, setEditing] = useState(false);
  const label = block.title ?? (block.type === 'text' ? 'Testo' : block.type === 'table' ? 'Tabella' : 'Video');

  return (
    <div className="block-card">
      <button type="button" className="block-head" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <ChevronRight size={16} className="chev" />
        <strong>{label}</strong>
        {block.type === 'text' && block.readingTimeMinutes !== null && (
          <span className="reading-time"><Clock size={11} style={{ verticalAlign: -1, marginRight: 3 }} />{block.readingTimeMinutes} min</span>
        )}
      </button>
      {open && (editing
        ? <EditBlockForm block={block} onCancel={() => setEditing(false)} pending={false} onSave={(patch) => { onUpdate(patch); setEditing(false); }} />
        : <BlockView block={block} />
      )}
      {canManage && open && !editing && (
        <div className="block-actions">
          <button type="button" className="icon-btn muted" style={{ width: 28, height: 28 }} aria-label={`Modifica ${label}`} onClick={() => setEditing(true)}><Pencil size={14} /></button>
          <button type="button" className="icon-btn muted" style={{ width: 28, height: 28 }} aria-label={`Elimina ${label}`} onClick={onDelete}><Trash2 size={14} /></button>
        </div>
      )}
    </div>
  );
}

export function BlockList({
  blocks, canManage, onMove, onUpdate, onDelete,
}: {
  blocks: SectionBlock[]; canManage: boolean; onMove: (index: number, dir: -1 | 1) => void;
  onUpdate: (id: string, patch: SectionBlockUpdateInput) => void; onDelete: (block: SectionBlock) => void;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      {blocks.map((b, i) => (
        <div key={b.id} style={{ position: 'relative' }}>
          <BlockRow block={b} canManage={canManage} onUpdate={(patch) => onUpdate(b.id, patch)} onDelete={() => onDelete(b)} />
          {canManage && blocks.length > 1 && (
            <span className="order-btns" style={{ position: 'absolute', top: 8, right: 8 }}>
              <button type="button" className="icon-btn muted" style={{ width: 24, height: 24 }} aria-label="Sposta su" disabled={i === 0} onClick={() => onMove(i, -1)}><ArrowUp size={13} /></button>
              <button type="button" className="icon-btn muted" style={{ width: 24, height: 24 }} aria-label="Sposta giù" disabled={i === blocks.length - 1} onClick={() => onMove(i, 1)}><ArrowDown size={13} /></button>
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export function NewBlockForm({ type, onCancel, onSubmit, pending }: { type: SectionBlockType; onCancel: () => void; onSubmit: (v: SectionBlockInput) => void; pending: boolean }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [columns, setColumns] = useState(['Colonna 1', 'Colonna 2']);
  const [rows, setRows] = useState<string[][]>([['', '']]);
  const [url, setUrl] = useState('');
  const [caption, setCaption] = useState('');

  const submit = () => {
    if (type === 'text') { if (!body.trim()) return; onSubmit({ type, title: title.trim() || null, content: { body: body.trim() } }); }
    else if (type === 'table') { if (columns.every((c) => !c.trim())) return; onSubmit({ type, title: title.trim() || null, content: { columns, rows } }); }
    else { if (!url.trim()) return; onSubmit({ type, title: title.trim() || null, content: { url: url.trim(), caption: caption.trim() || null } }); }
  };

  return (
    <div className="block-card" style={{ padding: 14 }}>
      <div className="form">
        <TextField label="Titolo (facoltativo)" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        {type === 'text' && <TextArea label="Testo" rows={5} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Scrivi il contenuto…" />}
        {type === 'table' && <TableGridEditor columns={columns} rows={rows} onChange={(c, r) => { setColumns(c); setRows(r); }} />}
        {type === 'video' && (
          <>
            <TextField label="Link video" placeholder="https://www.youtube.com/watch?v=..." value={url} onChange={(e) => setUrl(e.target.value)} />
            <TextField label="Didascalia (facoltativa)" value={caption} onChange={(e) => setCaption(e.target.value)} />
          </>
        )}
        <FormActions onCancel={onCancel} pending={pending} disabled={false} onSave={submit} />
      </div>
    </div>
  );
}
